import React, { useState, useEffect, useCallback } from 'react';
import { useApi, apiGet } from '../hooks/useApi';
import { RefreshCw, Pause, Play } from 'lucide-react';
import AccountTypeFilter from '../components/AccountTypeFilter';

const PAGE_SIZE_OPTIONS = [50, 100, 200, 500];

const makeEaKey = (ea) => `${ea.account_id || 0}:${ea.magic || 0}:${encodeURIComponent((ea.ea_name || '').trim())}`;
const makeTradeEaKey = (trade) => `${trade.account_id || 0}:${trade.magic || 0}:${encodeURIComponent((trade.ea_name || '').trim())}`;

export default function Trades() {
  const [accountType, setAccountType] = useState('all');
  const [selectedAccount, setSelectedAccount] = useState('all');
  const scopeParams = new URLSearchParams();
  if (selectedAccount !== 'all') scopeParams.set('account_id', selectedAccount);
  else if (accountType !== 'all') scopeParams.set('type_filter', accountType);
  const scopeQuery = scopeParams.toString() ? `?${scopeParams.toString()}` : '';

  const { data: activeTrades, loading: activeLoading, refetch: refetchActive } = useApi(`/trades/active${scopeQuery}`);
  const { data: tradeSummary } = useApi(`/trades/summary${scopeQuery}`);
  const { data: accounts } = useApi('/accounts');
  const { data: eaStats } = useApi(`/ea-stats${scopeQuery}`);
  const { data: appSettings } = useApi('/settings');
  const { data: allSymbols } = useApi(`/trades/symbols${scopeQuery}`);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [filters, setFilters] = useState({ symbol: '', direction: '', ea: '', result: '', dateFrom: '', dateTo: '', page: 1 });
  const [pageSize, setPageSize] = useState(200);
  const [activeFilter, setActiveFilter] = useState({ pair: 'all', ea: 'all', sort: 'profit_desc' });
  const refreshInterval = appSettings?.refresh_interval || 60;
  const [refreshCountdown, setRefreshCountdown] = useState(60);
  const [isPaused, setIsPaused] = useState(false);

  const visibleAccounts = React.useMemo(() => {
    if (accountType === 'all') return accounts || [];
    const wantDemo = accountType === 'demo';
    return (accounts || []).filter(account => Boolean(account.is_demo) === wantDemo);
  }, [accounts, accountType]);

  const resetScopedFilters = () => {
    setFilters(current => ({ ...current, symbol: '', ea: '', page: 1 }));
    setActiveFilter(current => ({ ...current, pair: 'all', ea: 'all' }));
  };

  const handleAccountTypeChange = (nextType) => {
    setAccountType(nextType);
    setSelectedAccount('all');
    resetScopedFilters();
  };

  const handleAccountChange = (nextAccount) => {
    setSelectedAccount(nextAccount);
    resetScopedFilters();
  };

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.symbol) params.set('symbol', filters.symbol);
      if (filters.direction) params.set('direction', filters.direction);
      if (filters.ea) params.set('ea_key', filters.ea);
      if (filters.result) params.set('result', filters.result);
      if (filters.dateFrom) params.set('date_from', filters.dateFrom);
      if (filters.dateTo) params.set('date_to', filters.dateTo);
      if (selectedAccount && selectedAccount !== 'all') params.set('account_id', selectedAccount);
      else if (accountType !== 'all') params.set('type_filter', accountType);
      params.set('page', filters.page);
      params.set('limit', String(pageSize));
      const data = await apiGet(`/trades/history?${params.toString()}`);
      setHistory(data);
    } catch (e) {
      setHistory({ trades: [], total: 0, page: 1, pages: 0 });
    }
    setHistoryLoading(false);
  }, [filters, selectedAccount, accountType, pageSize]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Auto-refresh countdown
  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setRefreshCountdown(prev => {
        if (prev <= 1) {
          refetchActive();
          return refreshInterval;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isPaused, refetchActive, refreshInterval]);

  // Reset countdown when settings load
  useEffect(() => {
    if (refreshInterval) setRefreshCountdown(refreshInterval);
  }, [refreshInterval]);

  const eaOptions = React.useMemo(() => {
    return (eaStats || [])
      .map(s => {
        const name = s.ea_name && !s.ea_name.startsWith('EA_') ? s.ea_name : `Magic ${s.magic}`;
        return {
          key: makeEaKey(s),
          name,
          magic: s.magic,
          account_id: s.account_id,
          label: `${name} (#${s.magic}${s.account_id ? ` / A${s.account_id}` : ''})`,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name) || String(a.account_id || '').localeCompare(String(b.account_id || '')) || Number(a.magic || 0) - Number(b.magic || 0));
  }, [eaStats]);

  // Filter and sort active trades
  const filteredActiveTrades = React.useMemo(() => {
    let trades = [...(activeTrades || [])];

    if (selectedAccount !== 'all') {
      trades = trades.filter(t => String(t.account_id) === selectedAccount);
    }
    if (activeFilter.pair !== 'all') {
      trades = trades.filter(t => t.symbol === activeFilter.pair);
    }
    if (activeFilter.ea !== 'all') {
      trades = trades.filter(t => makeTradeEaKey(t) === activeFilter.ea);
    }

    switch (activeFilter.sort) {
      case 'profit_desc': trades.sort((a, b) => b.profit - a.profit); break;
      case 'profit_asc': trades.sort((a, b) => a.profit - b.profit); break;
      case 'lots_desc': trades.sort((a, b) => b.lots - a.lots); break;
      case 'duration_desc': trades.sort((a, b) => (b.duration_seconds || 0) - (a.duration_seconds || 0)); break;
      case 'duration_asc': trades.sort((a, b) => (a.duration_seconds || 0) - (b.duration_seconds || 0)); break;
      default: break;
    }
    return trades;
  }, [activeTrades, selectedAccount, activeFilter, eaOptions]);

  const floatingPnl = filteredActiveTrades.reduce((sum, t) => sum + (t.profit || 0), 0);
  const totalLots = filteredActiveTrades.reduce((sum, t) => sum + (t.lots || 0), 0);

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Trades</h1>
          <p className="text-sm text-gray-400">Your complete trading history</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <AccountTypeFilter value={accountType} onChange={handleAccountTypeChange} />
          <select
            value={selectedAccount}
            onChange={e => handleAccountChange(e.target.value)}
            className="bg-dark-card border border-dark-border rounded-lg px-3 py-1.5 text-sm text-gray-300 w-full sm:w-auto"
          >
            <option value="all">All Accounts</option>
            {visibleAccounts.map(a => (
              <option key={a.id} value={a.id}>{a.account_number} {a.broker ? `(${a.broker})` : ''} {a.is_demo ? '[DEMO]' : '[REAL]'}</option>
            ))}
          </select>
          <span className="text-xs text-gray-400">Refresh: <span className="text-accent-green font-bold">{refreshCountdown}s</span></span>
          <button
            onClick={() => setIsPaused(!isPaused)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold ${isPaused ? 'bg-accent-green text-dark-bg' : 'bg-accent-orange text-dark-bg'}`}
          >
            {isPaused ? <Play size={12} /> : <Pause size={12} />}
          </button>
          <button onClick={refetchActive} className="flex items-center gap-1 px-3 py-1.5 bg-dark-card border border-dark-border rounded-lg text-sm text-gray-300 hover:text-white">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Active Trades */}
      <div className="card p-3 lg:p-4">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-accent-green">⚡</span>
          <h3 className="font-semibold">Active Trades</h3>
          <span className="text-xs bg-accent-green/20 text-accent-green px-2 py-0.5 rounded-full">
            {filteredActiveTrades.length} open
          </span>
        </div>

        {/* Active Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:gap-4 mb-4">
          <div>
            <span className="text-xs text-gray-500 block">FLOATING P/L</span>
            <span className={`text-lg lg:text-xl font-bold font-mono ${floatingPnl >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
              ${floatingPnl.toFixed(2)}
            </span>
          </div>
          <div>
            <span className="text-xs text-gray-500 block">TOTAL LOTS</span>
            <span className="text-lg lg:text-xl font-bold font-mono">{totalLots.toFixed(2)}</span>
          </div>
          <div>
            <span className="text-xs text-gray-500 block">POSITIONS</span>
            <span className="text-lg lg:text-xl font-bold">{filteredActiveTrades.length}</span>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-4 py-3 px-3 lg:px-4 bg-dark-bg rounded-lg border border-dark-border">
          <select
            value={activeFilter.pair}
            onChange={e => setActiveFilter(prev => ({ ...prev, pair: e.target.value }))}
            className="bg-transparent border-none text-sm text-accent-green font-medium cursor-pointer outline-none w-full sm:w-auto"
          >
            <option value="all" className="bg-dark-card">All Pairs</option>
            {[...new Set((activeTrades || []).map(t => t.symbol))].map(sym => (
              <option key={sym} value={sym} className="bg-dark-card">{sym}</option>
            ))}
          </select>

          <select
            value={activeFilter.ea}
            onChange={e => setActiveFilter(prev => ({ ...prev, ea: e.target.value }))}
            className="bg-transparent border-none text-sm text-accent-green font-medium cursor-pointer outline-none w-full sm:w-auto"
          >
            <option value="all" className="bg-dark-card">All EAs</option>
            {eaOptions.map(ea => (
              <option key={ea.key} value={ea.key} className="bg-dark-card">
                {ea.label}
              </option>
            ))}
          </select>

          <select
            value={activeFilter.sort}
            onChange={e => setActiveFilter(prev => ({ ...prev, sort: e.target.value }))}
            className="bg-transparent border-none text-sm text-gray-400 cursor-pointer outline-none w-full sm:w-auto"
          >
            <option value="profit_desc" className="bg-dark-card">↑↓ Highest P/L</option>
            <option value="profit_asc" className="bg-dark-card">↑↓ Lowest P/L</option>
            <option value="lots_desc" className="bg-dark-card">↑↓ Largest Lots</option>
            <option value="duration_desc" className="bg-dark-card">↑↓ Longest Duration</option>
            <option value="duration_asc" className="bg-dark-card">↑↓ Shortest Duration</option>
          </select>
        </div>

        {/* Active Trades Table */}
        <div className="overflow-x-auto -mx-3 lg:-mx-4 px-3 lg:px-4">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-gray-500 border-b border-dark-border text-left text-xs">
                <th className="py-2 px-3">Account</th>
                <th className="py-2 px-3">Symbol</th>
                <th className="py-2 px-3">Type</th>
                <th className="py-2 px-3">Lots</th>
                <th className="py-2 px-3">Open Price</th>
                <th className="py-2 px-3">Floating P/L</th>
                <th className="py-2 px-3">Duration</th>
                <th className="py-2 px-3">EA</th>
              </tr>
            </thead>
            <tbody>
              {filteredActiveTrades.map(trade => (
                <tr key={trade.ticket} className="border-b border-dark-border/50 hover:bg-dark-hover">
                  <td className="py-2 px-3 text-gray-400 text-xs">
                    {(() => { const a = (accounts || []).find(acc => acc.id === trade.account_id); return a ? (a.broker || `#${a.account_number}`) : `#${trade.account_id}`; })()}
                    {(() => { const a = (accounts || []).find(acc => acc.id === trade.account_id); return a ? (
                      <span className={`ml-1.5 text-[9px] px-1.5 py-0.5 rounded font-bold ${a.is_demo ? 'bg-accent-orange/20 text-accent-orange' : 'bg-accent-green/20 text-accent-green'}`}>{a.is_demo ? 'Demo' : 'Real'}</span>
                    ) : null; })()}
                  </td>
                  <td className="py-2 px-3 font-bold">{trade.symbol}</td>
                  <td className="py-2 px-3">
                    <span className={`flex items-center gap-1 text-xs font-bold ${trade.type === 'BUY' ? 'text-accent-green' : 'text-accent-red'}`}>
                      {trade.type === 'BUY' ? '↗' : '↘'} {trade.type}
                    </span>
                  </td>
                  <td className="py-2 px-3">{trade.lots}</td>
                  <td className="py-2 px-3">{trade.open_price}</td>
                  <td className={`py-2 px-3 font-semibold ${trade.profit >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                    {trade.profit >= 0 ? '+' : ''}${trade.profit.toFixed(2)}
                  </td>
                  <td className="py-2 px-3 text-gray-400 text-xs">{formatDuration(trade.duration_seconds)}</td>
                  <td className="py-2 px-3 text-accent-purple text-xs">{trade.ea_name || `Magic ${trade.magic}`}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredActiveTrades.length === 0 && (
            <p className="text-gray-500 text-center py-6 text-sm">No active trades</p>
          )}
        </div>
      </div>

      {/* Closed Trades Summary */}
      {tradeSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:gap-4">
          <div className="stat-card card-glow">
            <span className="text-xs text-gray-500">Closed Trades</span>
            <span className="text-lg lg:text-xl font-bold">{tradeSummary.total_closed_trades}</span>
          </div>
          <div className="stat-card card-glow">
            <span className="text-xs text-gray-500">Win Rate</span>
            <span className="text-lg lg:text-xl font-bold text-accent-green">{tradeSummary.win_rate}%</span>
          </div>
          <div className="stat-card card-glow">
            <span className="text-xs text-gray-500">Net Profit</span>
            <span className={`text-lg lg:text-xl font-bold ${tradeSummary.total_profit >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
              ${tradeSummary.total_profit.toLocaleString(undefined, {minimumFractionDigits: 2})}
            </span>
          </div>
          <div className="stat-card card-glow">
            <span className="text-xs text-gray-500">Commissions</span>
            <span className="text-lg lg:text-xl font-bold text-accent-red">
              ${(tradeSummary.total_commission || 0).toFixed(2)}
            </span>
          </div>
        </div>
      )}

      {/* Trade History */}
      <div className="card p-3 lg:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <h3 className="font-semibold">Trade History ({history?.total || 0} trades)</h3>
          <span className="text-xs text-gray-400">Newest First</span>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 lg:gap-3 mb-4">
          <select
            value={filters.symbol}
            onChange={e => setFilters(f => ({ ...f, symbol: e.target.value, page: 1 }))}
            className="bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-300 w-full"
          >
            <option value="">All Symbols</option>
            {(allSymbols || []).map(sym => (
              <option key={sym} value={sym}>{sym}</option>
            ))}
          </select>
          <select
            value={filters.direction}
            onChange={e => setFilters(f => ({ ...f, direction: e.target.value, page: 1 }))}
            className="bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-300 w-full"
          >
            <option value="">All Directions</option>
            <option value="BUY">Buy</option>
            <option value="SELL">Sell</option>
          </select>
          <select
            value={filters.result || ''}
            onChange={e => setFilters(f => ({ ...f, result: e.target.value, page: 1 }))}
            className="bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-300 w-full"
          >
            <option value="">All Trades</option>
            <option value="win">Winners</option>
            <option value="loss">Losers</option>
          </select>
          <select
            value={filters.ea}
            onChange={e => setFilters(f => ({ ...f, ea: e.target.value, page: 1 }))}
            className="bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-300 w-full"
          >
            <option value="">All EAs</option>
            {eaOptions.map(ea => (
              <option key={ea.key} value={ea.key}>
                {ea.label}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value, page: 1 }))}
            className="bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-300 w-full"
            placeholder="From"
            title="From date"
          />
          <input
            type="date"
            value={filters.dateTo}
            onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value, page: 1 }))}
            className="bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-300 w-full"
            placeholder="To"
            title="To date"
          />
        </div>

        {/* History Table */}
        <div className="overflow-x-auto -mx-3 lg:-mx-4 px-3 lg:px-4">
          <table className="w-full text-xs min-w-[800px]">
            <thead>
              <tr className="text-gray-500 border-b border-dark-border text-left">
                <th className="py-2 px-2">Account</th>
                <th className="py-2 px-2">Symbol</th>
                <th className="py-2 px-2">Type</th>
                <th className="py-2 px-2">Lots</th>
                <th className="py-2 px-2">Open</th>
                <th className="py-2 px-2">Close</th>
                <th className="py-2 px-2">Pips</th>
                <th className="py-2 px-2">Profit</th>
                <th className="py-2 px-2">Duration</th>
                <th className="py-2 px-2">EA</th>
                <th className="py-2 px-2">Date</th>
              </tr>
            </thead>
            <tbody>
              {(history?.trades || []).map(trade => (
                <tr key={trade.ticket} className="border-b border-dark-border/50 hover:bg-dark-hover">
                  <td className="py-2 px-2 text-gray-400">
                    {(() => { const a = (accounts || []).find(acc => acc.id === trade.account_id); return a ? (a.broker || `#${a.account_number}`) : `#${trade.account_id}`; })()}
                    {(() => { const a = (accounts || []).find(acc => acc.id === trade.account_id); return a ? (
                      <span className={`ml-1.5 text-[9px] px-1.5 py-0.5 rounded font-bold ${a.is_demo ? 'bg-accent-orange/20 text-accent-orange' : 'bg-accent-green/20 text-accent-green'}`}>{a.is_demo ? 'Demo' : 'Real'}</span>
                    ) : null; })()}
                  </td>
                  <td className="py-2 px-2 font-bold">{trade.symbol}</td>
                  <td className="py-2 px-2">
                    <span className={`font-bold ${trade.type === 'BUY' ? 'text-accent-green' : 'text-accent-red'}`}>
                      {trade.type === 'BUY' ? '↗' : '↘'} {trade.type}
                    </span>
                  </td>
                  <td className="py-2 px-2">{trade.lots}</td>
                  <td className="py-2 px-2">{trade.open_price}</td>
                  <td className="py-2 px-2">{trade.close_price}</td>
                  <td className={`py-2 px-2 ${trade.pips >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                    {trade.pips >= 0 ? '+' : ''}{trade.pips}
                  </td>
                  <td className={`py-2 px-2 font-semibold ${trade.profit >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                    {trade.profit >= 0 ? '+' : ''}${trade.profit.toFixed(2)}
                  </td>
                  <td className="py-2 px-2 text-gray-400">{formatDuration(trade.duration_seconds)}</td>
                  <td className="py-2 px-2 text-accent-purple">{trade.ea_name || `Magic ${trade.magic}`}</td>
                  <td className="py-2 px-2 text-gray-400">{trade.close_time ? new Date(trade.close_time).toLocaleDateString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(history?.trades || []).length === 0 && (
            <p className="text-gray-500 text-center py-6 text-sm">No trade history</p>
          )}
        </div>

        {/* Pagination */}
        {history && history.pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4">
            <button
              disabled={filters.page <= 1}
              onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}
              className="px-3 py-1 rounded border border-dark-border text-sm disabled:opacity-30"
            >
              Prev
            </button>
            <span className="text-sm text-gray-400">Page {filters.page} of {history.pages} ? showing {(history.trades || []).length} of {history.total}</span>
            <button
              disabled={filters.page >= history.pages}
              onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}
              className="px-3 py-1 rounded border border-dark-border text-sm disabled:opacity-30"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function formatDuration(seconds) {
  if (!seconds) return '-';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}




